import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { ConnectorRow, OwnerConnection } from '../../types/aai';
import { Action, Card, Chip, Drawer, FactList, Kicker } from '../../components/aa';
import type { ChipTone } from '../../components/aa';
import { relativeTime } from '../partner/format';
import { ManageInWorkspace, workspaceGitLine } from '../../components/settings/ManageInWorkspace';
import { isVersionControl, versionControlStatus } from '../../components/settings/versionControl';

/*
 * One company connection, in business terms.
 *
 * What it is, who owns it, whether it is healthy, and how many people have
 * connected the same app personally (a count, never names). The two actions
 * are the two the Owner really holds: disconnecting the company connection and
 * opening the company connection settings. Both need `aai_manage_connectors`.
 *
 * Seeing a connection is not permission to browse it. Records, credentials and
 * OAuth scopes stay with the connection owner and Admin, and the panel says so
 * with a disabled control rather than a missing one.
 */

export const CONNECTION_STATUS: Record<string, { label: string; tone: ChipTone }> = {
  connected: { label: 'Ready', tone: 'ok' },
  pending: { label: 'Pending', tone: 'warn' },
  needs_reconnect: { label: 'Needs reconnect', tone: 'warn' },
  error: { label: 'Error', tone: 'err' },
  expired: { label: 'Expired', tone: 'warn' },
  disconnected: { label: 'Disconnected', tone: 'neutral' },
};

export const statusChip = (status: string | null | undefined): { label: string; tone: ChipTone } =>
  CONNECTION_STATUS[status || ''] || { label: status || 'Not connected', tone: 'neutral' };

export interface CompanyConnection {
  provider: string;
  label: string;
  category: string;
  enabled: boolean;
  row: OwnerConnection | null;
  membersConnected: number;
  /** The catalog row behind it, which for version control carries the Workspace's state. */
  catalogRow: ConnectorRow;
  versionControl: boolean;
}

/** The status a company connection shows: the Workspace's for version control, the shared row's otherwise. */
export const companyConnectionStatus = (c: CompanyConnection): string | null | undefined => {
  if (c.versionControl) return versionControlStatus(c.catalogRow);
  return c.row?.status;
};

export const joinConnections = (
  catalog: ConnectorRow[],
  rows: OwnerConnection[],
  coverage: { provider: string; members_connected: number }[],
): CompanyConnection[] => {
  const byProvider = new Map(rows.map((r) => [r.provider, r]));
  const covered = new Map(coverage.map((c) => [c.provider, c.members_connected]));
  return catalog
    .filter((c) => byProvider.has(c.slug) || c.organization_status || (c.enabled && isVersionControl(c)))
    .map((c) => ({
      provider: c.slug,
      label: c.label,
      category: c.category_label,
      enabled: c.enabled,
      row: byProvider.get(c.slug) || null,
      membersConnected: covered.get(c.slug) || 0,
      catalogRow: c,
      versionControl: isVersionControl(c),
    }));
};

export const ConnectionDrawer: React.FC<{
  connection: CompanyConnection;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
}> = ({ connection, canManage, onClose, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const status = statusChip(companyConnectionStatus(connection));

  const disconnect = async () => {
    setBusy(true);
    try {
      await aaiApi.revokeConnector(connection.provider, 'organization');
      toast.success(`${connection.label} is no longer shared with the company.`);
      onChanged();
      onClose();
    } catch {
      toast.error('Could not disconnect it.');
    } finally {
      setBusy(false);
    }
  };

  const health = (): string => {
    if (connection.versionControl) return workspaceGitLine(connection.catalogRow);
    if (!connection.row) return 'Not recorded';
    if (connection.row.error_message) return connection.row.error_message;
    if (connection.row.last_sync_at) return `Last sync ${relativeTime(connection.row.last_sync_at)}`;
    return status.label;
  };

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Company connection</Kicker>}
      badge={(
        <>
          <Chip tone={status.tone} dot>{status.label}</Chip>
          {!connection.enabled && <Chip tone="warn">Switched off for the company</Chip>}
        </>
      )}
      title={connection.label}
      sub="Shared with the company by its owner. Who owns it, whether it is healthy and who else has connected it. The records stay in the app."
      footer={(
        <div className="flex flex-wrap items-center gap-3">
          {connection.versionControl && <ManageInWorkspace row={connection.catalogRow} />}
          {!connection.versionControl && <Action size="lg" variant="quiet" to="/settings?tab=connectors">Company connection settings</Action>}
          {canManage && connection.row && !connection.versionControl && (
            <Action size="lg" variant="quiet" onClick={disconnect} disabled={busy} reason="Working">Disconnect from the company</Action>
          )}
          <Action size="lg" variant="ghost" onClick={onClose}>Close</Action>
        </div>
      )}
    >
      <FactList
        facts={[
          { label: 'Connection owner', value: connection.versionControl ? 'Your Workspace' : connection.row?.owner_email || 'Not recorded' },
          { label: 'Scope', value: 'Company', right: <Chip>Shared by the owner</Chip> },
          { label: 'Category', value: connection.category },
          { label: 'Health', value: health() },
          { label: 'Personal connections', value: `${connection.membersConnected} ${connection.membersConnected === 1 ? 'person has' : 'people have'} connected it themselves` },
        ]}
      />

      <Card pad="md" className="mt-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="text-[13px] font-semibold text-ink">Records</span>
            <Chip tone="private">Source rights apply</Chip>
          </div>
          <Action size="sm" variant="quiet" icon={<Lock size={14} />} disabled reason="Browsing a source needs a source right from the connection owner. The Owner role does not include one.">
            Browse records
          </Action>
        </div>
        <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">
          Seeing this connection is not permission to browse it. Credentials and OAuth scopes stay with the connection owner and Admin.
        </p>
      </Card>

      <p className="m-0 mt-5 text-[12.5px] leading-[20px] text-ink-dim">
        Purpose, mode and an access review date are not recorded by the platform yet, so they are not shown here.
      </p>
    </Drawer>
  );
};
